/**
 * Account screen: guest convert, password+OTP or magic-link login, Orbit Pass.
 */
export default class AccountScreen {
  constructor({ auth, onBack, onAdmin, onCatalog }) {
    this.auth = auth;
    this.onBack = onBack;
    this.onAdmin = onAdmin;
    this.onCatalog = onCatalog;
    this._authTab = 'create';
    this._loginTab = 'password';
    this._interval = 'month';

    this.chipName = document.getElementById('account-chip-name');
    this.chip = document.getElementById('btn-account');
    this.subtitle = document.getElementById('account-subtitle');
    this.username = document.getElementById('account-username');
    this.detail = document.getElementById('account-detail');
    this.verifyBanner = document.getElementById('account-verify-banner');
    this.offlineBanner = document.getElementById('account-offline-banner');
    this.guestAuth = document.getElementById('account-guest-auth');
    this.logoutCard = document.getElementById('account-logout-card');
    this.adminBtn = document.getElementById('btn-admin');
    this.flashEl = document.getElementById('account-flash');

    document.getElementById('btn-account-back')?.addEventListener('click', () => this.onBack?.());
    document.getElementById('btn-logout')?.addEventListener('click', async () => {
      await this.auth.logout();
      this.refresh();
    });
    this.adminBtn?.addEventListener('click', () => this.onAdmin?.());
    document.getElementById('btn-resend-verification')?.addEventListener('click', async (event) => {
      event.target.disabled = true;
      await this.auth.resendVerification();
      event.target.textContent = 'ENVOYÉ ✓';
    });

    document.getElementById('form-convert')?.addEventListener('submit', (e) => this._onConvert(e));
    document.getElementById('form-login')?.addEventListener('submit', (e) => this._onLogin(e));
    document.getElementById('form-otp')?.addEventListener('submit', (e) => this._onOtp(e));
    document.getElementById('form-magic')?.addEventListener('submit', (e) => this._onMagic(e));
    document.getElementById('btn-otp-back')?.addEventListener('click', () => this._showOtp(false));

    this.guestAuth?.addEventListener('click', (e) => {
      const authTab = e.target.closest('[data-auth-tab]');
      if (authTab) this._setAuthTab(authTab.dataset.authTab);
      const loginTab = e.target.closest('[data-login-tab]');
      if (loginTab) this._setLoginTab(loginTab.dataset.loginTab);
    });

    document.getElementById('btn-pass-month')?.addEventListener('click', () => this._buy('month'));
    document.getElementById('btn-pass-year')?.addEventListener('click', () => this._buy('year'));
    document.getElementById('btn-pass-portal')?.addEventListener('click', () => this._portal());

    this.refresh();
  }

  flash(message, tone = 'ok') {
    if (!this.flashEl) return;
    this.flashEl.textContent = message || '';
    this.flashEl.classList.toggle('hidden', !message);
    this.flashEl.dataset.tone = tone;
  }

  showLogin() {
    this._setAuthTab('login');
    this._setLoginTab('password');
  }

  refresh() {
    const user = this.auth.user;

    if (this.chipName) {
      this.chipName.textContent = user ? user.username : 'HORS LIGNE';
    }
    this.chip?.classList.toggle('account-chip--guest', !user || user.isGuest);
    this.chip?.classList.toggle('account-chip--premium', !!(user && user.isPremium));

    if (!this.username) return;

    if (!user) {
      this.subtitle.textContent = 'HORS LIGNE';
      this.username.textContent = '—';
      this.detail.textContent = 'Tes scores restent enregistrés sur cet appareil.';
      this.offlineBanner?.classList.remove('hidden');
      this.verifyBanner?.classList.add('hidden');
      this.guestAuth?.classList.add('hidden');
      this.logoutCard?.classList.add('hidden');
      this._renderPass(null);
      return;
    }

    this.offlineBanner?.classList.add('hidden');
    this.username.textContent = user.username;

    if (user.isGuest) {
      this.subtitle.textContent = 'COMPTE INVITÉ';
      this.detail.textContent = 'Ce compte invité te suit sur cet appareil. Valide-le pour te reconnecter ailleurs.';
      this.verifyBanner?.classList.add('hidden');
      this.guestAuth?.classList.remove('hidden');
      this.logoutCard?.classList.add('hidden');
      this._setAuthTab(this._authTab);
      this._setLoginTab(this._loginTab);
    } else {
      this.subtitle.textContent = user.isPremium ? 'ORBIT PASS ACTIF' : 'COMPTE VALIDÉ';
      this.detail.textContent = user.email || '';
      this.verifyBanner?.classList.toggle('hidden', user.emailVerified);
      this.guestAuth?.classList.add('hidden');
      this.logoutCard?.classList.remove('hidden');
      this.adminBtn?.classList.toggle('hidden', !user.isAdmin);
    }
    this._renderPass(user);
  }

  _renderPass(user) {
    const hint = document.getElementById('pass-hint');
    const prices = document.getElementById('pass-prices');
    const portal = document.getElementById('btn-pass-portal');
    const fine = document.getElementById('pass-fine');
    if (!user) {
      if (hint) hint.textContent = 'Reconnecte-toi pour gérer l’Orbit Pass.';
      prices?.classList.add('hidden');
      portal?.classList.add('hidden');
      return;
    }
    if (user.isPremium) {
      if (hint) hint.textContent = 'Coffre et archives ouverts. Le daily reste gratuit pour tout le monde.';
      prices?.classList.add('hidden');
      portal?.classList.toggle('hidden', user.isGuest);
      if (fine) fine.textContent = user.premiumUntil
        ? `Actif jusqu’au ${new Date(user.premiumUntil).toLocaleDateString('fr-FR')}.`
        : 'Pass actif. Gère ou résilie depuis le portail Stripe.';
      return;
    }
    if (user.isGuest) {
      if (hint) hint.textContent = 'Crée un compte (onglet Créer) pour souscrire — daily et Radio restent gratuits.';
      prices?.classList.add('hidden');
      portal?.classList.add('hidden');
      if (fine) fine.textContent = '2,49 € / mois ou 19,90 € / an · essai 7 jours · résiliable.';
      return;
    }
    if (hint) hint.textContent = 'Daily et 5 titres Radio restent gratuits. Le coffre et les archives, c’est l’abonnement.';
    prices?.classList.remove('hidden');
    portal?.classList.add('hidden');
    if (fine) fine.textContent = 'Renouvellement automatique. Tu peux résilier depuis le portail Stripe.';
  }

  _setAuthTab(tab) {
    this._authTab = tab === 'login' ? 'login' : 'create';
    const create = this._authTab === 'create';
    document.getElementById('panel-create')?.classList.toggle('hidden', !create);
    document.getElementById('panel-login')?.classList.toggle('hidden', create);
    document.getElementById('tab-create')?.classList.toggle('is-active', create);
    document.getElementById('tab-login')?.classList.toggle('is-active', !create);
    document.getElementById('tab-create')?.setAttribute('aria-selected', String(create));
    document.getElementById('tab-login')?.setAttribute('aria-selected', String(!create));
  }

  _setLoginTab(tab) {
    this._loginTab = tab === 'magic' ? 'magic' : 'password';
    const magic = this._loginTab === 'magic';
    const otpOpen = !document.getElementById('form-otp')?.classList.contains('hidden');
    document.getElementById('form-login')?.classList.toggle('hidden', magic || otpOpen);
    document.getElementById('form-magic')?.classList.toggle('hidden', !magic);
    if (magic) this._showOtp(false);
    document.getElementById('tab-password')?.classList.toggle('is-active', !magic);
    document.getElementById('tab-magic')?.classList.toggle('is-active', magic);
  }

  _showOtp(on, hint) {
    document.getElementById('form-login')?.classList.toggle('hidden', on);
    document.getElementById('form-otp')?.classList.toggle('hidden', !on);
    if (hint) {
      const el = document.getElementById('otp-hint');
      if (el) el.textContent = hint;
    }
    if (on) document.getElementById('login-otp')?.focus();
  }

  _setError(id, message) {
    const error = document.getElementById(id);
    if (!error) return;
    error.textContent = message || '';
    error.classList.toggle('hidden', !message);
  }

  async _onConvert(event) {
    event.preventDefault();
    this._setError('convert-error');
    const result = await this.auth.convert({
      username: document.getElementById('convert-username')?.value.trim(),
      email: document.getElementById('convert-email')?.value.trim(),
      password: document.getElementById('convert-password')?.value
    });
    if (result.ok) {
      this.flash('Compte créé. Vérifie ton email, puis l’Orbit Pass est dispo.');
      this.refresh();
    } else {
      this._setError('convert-error', result.message);
    }
  }

  async _onLogin(event) {
    event.preventDefault();
    this._setError('login-error');
    const result = await this.auth.login({
      username: document.getElementById('login-username')?.value.trim(),
      password: document.getElementById('login-password')?.value
    });
    if (result.ok && result.needsOtp) {
      this._showOtp(true, `Code envoyé à ${result.emailHint}. Valable 10 minutes.`);
      return;
    }
    this._setError('login-error', result.message || 'Connexion impossible.');
  }

  async _onOtp(event) {
    event.preventDefault();
    this._setError('login-error');
    const result = await this.auth.verifyOtp(document.getElementById('login-otp')?.value);
    if (result.ok) {
      this._showOtp(false);
      this.flash('Connecté.');
      this.refresh();
      return;
    }
    this._setError('login-error', result.message);
  }

  async _onMagic(event) {
    event.preventDefault();
    this._setError('login-error');
    const result = await this.auth.requestMagic(document.getElementById('magic-email')?.value.trim());
    if (result.ok) {
      this.flash(result.message || 'Si un compte existe, un lien part maintenant.');
    } else {
      this._setError('login-error', result.message);
    }
  }

  async _buy(interval) {
    this.flash('');
    const result = await this.auth.startCheckout(interval);
    if (!result.ok) this.flash(result.message || 'Paiement indisponible.', 'err');
  }

  async _portal() {
    const result = await this.auth.openPortal();
    if (!result.ok) this.flash(result.message || 'Portail indisponible.', 'err');
  }
}
